import { isTerminalAction } from '@/store/game/rallyPredicates';
import { isContextAction } from './groupByPoint';
import type { HistoryAction, PointGroup } from './matchHistory.types';

/**
 * Ce que change une édition au découpage du set en points.
 *
 * - `ok` : le point garde sa fin, rien d'autre à demander ;
 * - `needs-closing` : l'action terminait le point et ne le termine plus — le
 *   coach doit saisir l'action qui le clôt, sinon le point fusionnerait avec
 *   le suivant et tout le set serait décalé d'un rallye ;
 * - `would-split` : l'action deviendrait terminale au milieu d'un point, les
 *   actions qui la suivent formeraient un rallye orphelin.
 */
export type EditImpact = 'ok' | 'needs-closing' | 'would-split';

/**
 * Dernière action de jeu du point (hors temps mort, changement…), par séquence :
 * le panneau range les actions du plus récent au plus ancien, l'ordre de la
 * liste ne dit donc rien.
 */
function lastPlayAction(point: PointGroup): HistoryAction | undefined {
  let last: HistoryAction | undefined;
  for (const a of point.actions) {
    if (isContextAction(a)) continue;
    if (!last || (a.sequence ?? 0) > (last.sequence ?? 0)) last = a;
  }
  return last;
}

export function editImpact(
  point: PointGroup,
  action: HistoryAction,
  next: { type: string; quality: string },
): EditImpact {
  const wasTerminal = action.isTerminal;
  const willBeTerminal = isTerminalAction(next.type, next.quality);

  if (wasTerminal && !willBeTerminal) {
    // Un point encore ouvert n'a pas de fin à préserver : l'action en cours
    // redevient simplement une action du rallye.
    return point.open ? 'ok' : 'needs-closing';
  }
  if (!wasTerminal && willBeTerminal) {
    return lastPlayAction(point)?.id === action.id ? 'ok' : 'would-split';
  }
  return 'ok';
}

/** Camp auteur d'une action de l'historique. */
export function actionSide(action: HistoryAction): 'home' | 'away' {
  if (action.teamSide) return action.teamSide;
  return action.isOpponent ? 'away' : 'home';
}

/**
 * « #7 Dupont ». Un joueur saisi par son seul numéro (deux équipes, mode
 * numéros) s'appelle déjà « #7 » : on ne le préfixe pas une seconde fois.
 */
export function playerLabel(name: string, number?: number): string {
  if (number == null || name.startsWith('#')) return name;
  return `#${number} ${name}`;
}
